import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { Button, TextArea, TextField } from "@warp-ds/react";
import { Alert } from "@warp-ds/react/components/alert";
import { type Ref, type SubmitEvent, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { useValidDealerItemContext } from "../../context/DealerItemProviderContext.tsx";
import { useTracking } from "../../utils/pulseHelper.ts";
import { validatePhone } from "../../utils/validatePhone.ts";

enum ContactFormState {
  Initial = "initial",
  Invalid = "invalid",
  Submitting = "submitting",
  Success = "success",
  InvalidEid = "invalidEid",
  InvalidLogin = "invalidLogin",
  Error = "error",
}

const empty = (message: string) => message.trim().length === 0;

export type ContactFormModalHandle = {
  open: () => void;
};

export const ContactFormModal = ({ ref }: { ref?: Ref<ContactFormModalHandle> }) => {
  const modalRef = useRef<WarpModal>(null);
  const [formState, setFormState] = useState<ContactFormState>(ContactFormState.Initial);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const focusRef = useRef<HTMLInputElement>(null);
  const { i18n } = useLingui();
  const { trackViewContactFormModal, trackCancelContactFormModal, trackSubmitContactForm } = useTracking();
  const {
    external: { isMobile, adViewType, forcePhoneExperiment },
  } = useValidDealerItemContext();

  const {
    itemData: { adId },
    external: { brand },
  } = useValidDealerItemContext();

  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.open();
      trackViewContactFormModal({ isSuccessView: false, adViewType });
    },
  }));

  useEffect(() => {
    if (formState === ContactFormState.Success) {
      trackViewContactFormModal({ isSuccessView: true, adViewType });
    }
  }, [trackViewContactFormModal, formState, adViewType]);

  const onHidden = () => {
    if (formStateRef.current !== ContactFormState.Success) {
      trackCancelContactFormModal();
    }
    setTextHelpText("");
    setPhoneHelpText("");
    setFormState(ContactFormState.Initial);
  };

  const title = i18n.t({
    id: "dealer.contactForm.title",
    message: "Contact us",
    comment: "Header for the contact form",
  });

  const errorText = i18n.t({
    id: "dealer.contactForm.errorText.generic",
    message: "An error occurred while sending your message.\n" + "Please try again later. (Error code: {errorCode})",
    values: { errorCode },
    comment:
      "Generic error text displayed when an unexpected error occurs while submitting the contact form, includes error code",
  });
  const [textHelpText, setTextHelpText] = useState("");
  const [phoneHelpText, setPhoneHelpText] = useState("");
  const formId = useId();

  const validateMessage = (): boolean => {
    if (!message) {
      setTextHelpText(
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.mandatory",
          message: "Must be filled out",
          comment: "Help text when field has changed and has no value, displayed underneath the text-area",
        }),
      );
      return false;
    }
    if (empty(message)) {
      setTextHelpText(
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.emptyText",
          message: "Enter a message for the seller",
          comment:
            "Help text when the field has changed and is empty except spaces, displayed underneath the text-area",
        }),
      );
      setMessage("");
      return false;
    }
    setTextHelpText("");
    return true;
  };

  const validatePhoneNo = (): boolean => {
    if (empty(phoneNumber) && !forcePhoneExperiment) {
      setPhoneNumber("");
      setPhoneHelpText("");
      return true;
    }
    if (!validatePhone(phoneNumber, brand)) {
      setPhoneHelpText(
        i18n.t({
          id: "dealer.contactForm.phone.helpText.invalid",
          message: "Enter a valid phone number",
          comment: "Help text when the phone number format is invalid, displayed underneath the phone number field",
        }),
      );
      return false;
    }
    setPhoneHelpText("");
    return true;
  };

  // Checks both fields and sets formState accordingly
  const updateFormState = () => {
    const msgValid = validateMessage();
    const phoneValid = validatePhoneNo();
    if (msgValid && phoneValid) {
      setFormState(ContactFormState.Initial);
    } else {
      setFormState(ContactFormState.Invalid);
    }
  };

  // Usage: validate only one field, then update form state
  const validate = (field: "message" | "phone" | "both") => {
    let valid = true;
    if (field === "message") valid = validateMessage();
    else if (field === "phone") valid = validatePhoneNo();
    else if (field === "both") valid = validateMessage() && validatePhoneNo();

    // Always update form state after validation
    updateFormState();
    return valid;
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const isValid = validate("both");

    if (!isValid) {
      return;
    } else {
      setFormState(ContactFormState.Submitting);
    }

    try {
      const response = await fetch(`/mobility/api/contact/${adId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          phoneNumber,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          if (data.message === "Not logged in") {
            setFormState(ContactFormState.InvalidLogin);
            return;
          } else if (data.message === "eID verification failed") {
            setFormState(ContactFormState.InvalidEid);
            return;
          } else {
            setErrorCode(response.status);
            throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
          }
        } else {
          setErrorCode(response.status);
          throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
        }
      }

      trackSubmitContactForm(phoneNumber);
      // Success - reset form and show success view
      setMessage("");
      setPhoneNumber("");
      setFormState(ContactFormState.Success);
    } catch (error) {
      console.error("Error sending contact form:", error);
      setFormState(ContactFormState.Error);
    }
  };

  return (
    <Modal ref={modalRef} onHidden={onHidden}>
      <ModalHeader slot="header" title={title} />
      <div slot="content">
        {formState !== ContactFormState.Success ? (
          <form id={formId} onSubmit={handleSubmit}>
            <TextArea
              label={i18n.t({
                id: "dealer.contactForm.textArea.label",
                message: "Message to the seller",
                comment: "Label for the message, explains that this text will be sent to the advertiser",
              })}
              placeholder={i18n.t({
                id: "dealer.contactForm.textArea.placeholder",
                message: "Write a message…",
                comment: "Placeholder for the message text area",
              })}
              value={message}
              disabled={formState === ContactFormState.Submitting}
              invalid={formState === ContactFormState.Invalid && textHelpText !== ""}
              helpText={textHelpText}
              aria-errormessage={
                formState === ContactFormState.Invalid && textHelpText !== "" ? textHelpText : undefined
              }
              minimumRows={5}
              className="mb-24"
              onChange={({ target: { value: newValue } }) => {
                if (formState === ContactFormState.Invalid) {
                  setFormState(ContactFormState.Initial);
                  setTextHelpText("");
                }
                setMessage(newValue);
              }}
              onBlur={() => {
                validate("message");
              }}
            />
            <TextField
              label={i18n.t({
                id: "dealer.contactForm.phone.label",
                message: "Phone number",
                comment: "Label for the phone number text field",
              })}
              placeholder={i18n.t({
                id: "dealer.contactForm.phone.placeholder",
                message: "Enter your phone number",
                comment:
                  "Placeholder for the phone number field, using the country code for each country. Only the default (english) is a text",
              })}
              type="tel"
              optional={!forcePhoneExperiment}
              disabled={formState === ContactFormState.Submitting}
              invalid={phoneHelpText !== ""}
              helpText={phoneHelpText}
              aria-errormessage={phoneHelpText !== "" ? phoneHelpText : undefined}
              ref={focusRef}
              value={phoneNumber}
              onChange={({ target: { value } }) => setPhoneNumber(value)}
              className={"mb-24"}
              onBlur={() => {
                setPhoneNumber((prevState) => prevState.trim().replace(/\s/g, ""));
                validate("phone");
              }}
            />
            <div className="text-xs s-text-subtle">
              <Trans
                id="dealer.contactForm.infoText"
                message="When you send this message, your name, email, and phone number are transferred to the seller, who will then be responsible for your data. The message is sent via your registered email. {brand} may review and block messages to prevent fraud."
                values={{ brand: brand.toUpperCase() }}
              />
              <div className="mt-8">
                <Button link className="text-xs" href="/my-page/my-account" target="_blank">
                  <Trans id="dealer.contactForm.profileLink" message="You can view and change your e-mail here" />
                </Button>
              </div>
            </div>
            <Alert show={formState === ContactFormState.InvalidLogin} type="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.contactForm.loginErrorText.title"
                  message="You have been logged out"
                  comment="Alert title shown on invalid login error"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.contactForm.loginErrorText.description"
                  message="It looks like your session has expired. Log in again to send your message to the seller."
                  comment="Alert description displayed when a user was unauthorised while submitting the contact form"
                />
              </p>
              <Button className="flex items-center gap-8" href="/auth/login" target="_blank">
                <Trans id="dealer.contactForm.loginErrorButton.text" message="Log in (opens in new window)" />
                <WIcon name="LinkExternal" />
              </Button>
            </Alert>
            <Alert show={formState === ContactFormState.InvalidEid} type="negative" className="mb-24">
              Invalid eID code here
            </Alert>
            <Alert show={formState === ContactFormState.Error} type="negative" className="mb-24">
              {errorText}
            </Alert>
          </form>
        ) : (
          <div className="flex flex-col gap-24">
            <Trans
              id="dealer.contactForm.success.text"
              message="Your message has been sent. The dealer will contact you as soon as possible."
              comment="Message shown when contact form is sent successfully"
            />
          </div>
        )}
      </div>
      <ModalFooter slot="footer">
        {formState !== ContactFormState.Success ? (
          <Button
            primary
            type="submit"
            form={formId}
            loading={formState === ContactFormState.Submitting}
            fullWidth={isMobile}
          >
            <Trans id="dealer.contactForm.submitButton.text" message="Send message" />
          </Button>
        ) : (
          <Button
            fullWidth={isMobile}
            className="self-end"
            onClick={() => {
              modalRef.current?.close();
            }}
          >
            <Trans id="dealer.modal.close.button" message="Close" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
